/**
 * Persistent orange/blue banner when /api/config reports mirror or staging.
 * Fixed at the top; offsets #strip via --env-banner-h so it never covers the account menu.
 */

import { useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { apiFetch } from '../../api/client'

const BANNER_H = 36

type EnvBannerConfig = {
  mirror?: boolean
  mirrorRestoredFrom?: string | null
  staging?: boolean
  stagingRestoredFrom?: string | null
}

export default function MirrorBanner() {
  const { data } = useQuery({
    queryKey: ['app-config-mirror'],
    queryFn: () => apiFetch<EnvBannerConfig>('GET', '/api/config'),
    staleTime: 60_000,
  })

  const isMirror = !!data?.mirror
  const isStaging = !isMirror && !!data?.staging
  const show = isMirror || isStaging

  useEffect(() => {
    const root = document.documentElement
    if (show) root.style.setProperty('--env-banner-h', `${BANNER_H}px`)
    else root.style.removeProperty('--env-banner-h')
    return () => {
      root.style.removeProperty('--env-banner-h')
    }
  }, [show])

  if (!show) return null

  const date = isMirror
    ? (data?.mirrorRestoredFrom || 'unknown')
    : (data?.stagingRestoredFrom || 'prod backup')
  const label = isMirror
    ? `MIRROR · restored from ${date} · scrubbed`
    : `STAGING · restored from ${date} · prod data copy`
  const background = isMirror ? '#ea580c' : '#1d4ed8'
  return (
    <div
      role="status"
      aria-live="polite"
      data-testid={isMirror ? 'mirror-banner' : 'staging-banner'}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        height: BANNER_H,
        zIndex: 9999,
        background,
        color: '#fff',
        fontFamily: 'ui-sans-serif, system-ui, sans-serif',
        fontSize: 13,
        fontWeight: 700,
        letterSpacing: '0.04em',
        textAlign: 'center',
        padding: '8px 12px',
        boxShadow: '0 1px 0 rgba(0,0,0,0.15)',
        pointerEvents: 'none',
      }}
    >
      {label}
    </div>
  )
}
